
import React, { useMemo, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  CircularProgress,
  Skeleton,
  Tooltip,
  useTheme,
  alpha
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  SwapHoriz as SwapHorizIcon,
  MoreVert as MoreVertIcon,
  Inbox as InboxIcon
} from '@mui/icons-material';
import { FixedSizeList as List } from 'react-window';
import { Document } from './types';
import { getStatusText, getStatusColor, isNewDocument, formatDate, getDocumentNumber, isErrorStatus } from './utils';

// --- Constants & Grid Config ---
// Adjusted column widths to be more compacted but flexible
const GRID_TEMPLATE_COLUMNS = 'minmax(120px, 0.5fr) 100px 150px minmax(150px, 1.2fr) minmax(150px, 1.2fr) 140px 70px 40px';
const MIN_WIDTH = 980; // Сумма фиксированных + мин. ширина для fr

interface DocumentListProps {
  documents: Document[];
  incomingDocuments: Document[];
  isLoading: boolean;
  isLoadingDocument: boolean;
  currentUsername: string;
  onDocumentClick: (document: Document) => void;
  onMenuClick: (event: React.MouseEvent<HTMLElement>, document: Document) => void;
  highlightedDocumentId?: string | null;
}

const DocumentList: React.FC<DocumentListProps> = ({
  documents,
  incomingDocuments,
  isLoading,
  isLoadingDocument,
  currentUsername,
  onDocumentClick,
  onMenuClick,
  highlightedDocumentId
}) => {
  const theme = useTheme();

  // Loading state
  if (isLoading) {
    return (
      <Paper elevation={0} variant="outlined" sx={{ width: '100%', overflow: 'hidden' }}>
        {/* Header Skeleton */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
          gap: 1,
          p: 1.5,
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
          minWidth: MIN_WIDTH
        }}>
          {[...Array(8)].map((_, i) => <Skeleton key={i} variant="text" width="80%" />)}
        </Box>
        {/* Rows Skeleton */}
        {[...Array(10)].map((_, index) => (
          <Box key={index} sx={{
            display: 'grid',
            gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
            gap: 1,
            p: 1.5,
            borderBottom: '1px solid',
            borderColor: 'divider',
            minWidth: MIN_WIDTH
          }}>
            {[...Array(8)].map((_, i) => <Skeleton key={i} variant="text" width={i === 2 ? 60 : '90%'} />)}
          </Box>
        ))}
      </Paper>
    );
  }

  // Empty state
  if (documents.length === 0 && incomingDocuments.length === 0) {
    return (
      <Paper elevation={0} variant="outlined" sx={{ width: '100%', overflow: 'hidden', minHeight: 400, display: 'flex', flexDirection: 'column' }}>
        <ListHeader />
        <Box sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 4,
          bgcolor: 'background.default'
        }}>
          <Box sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: 'action.hover',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
            color: 'text.secondary'
          }}>
            <InboxIcon sx={{ fontSize: 40 }} />
          </Box>
          <Typography variant="h6" color="text.primary" gutterBottom>
            Список документов пуст
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center" sx={{ maxWidth: 400 }}>
            Создайте новый документ или дождитесь поступления входящих перемещений
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Вычисляем высоту контейнера
  const listHeight = Math.min(documents.length * 54 + 50, window.innerHeight - 405);

  return (
    <Paper elevation={0} variant="outlined" sx={{ width: '100%', overflow: 'hidden', border: 'none' }}>
      {/* Фиксированный заголовок */}
      <Box sx={{ overflowX: 'auto', '&::-webkit-scrollbar': { height: 6 } }}>
        <Box sx={{ minWidth: MIN_WIDTH }}>
          <ListHeader />

          {/* Виртуализированный список */}
          <List
            height={listHeight}
            itemCount={documents.length}
            itemSize={54} // Увеличили высоту строки для "воздуха"
            width="100%"
            overscanCount={5}
            style={{ overflowX: 'hidden' }}
          >
            {({ index, style }) => (
              <VirtualizedDocumentRow
                key={documents[index].id}
                document={documents[index]}
                currentUsername={currentUsername}
                isLoadingDocument={isLoadingDocument}
                onDocumentClick={onDocumentClick}
                onMenuClick={onMenuClick}
                isHighlighted={highlightedDocumentId === documents[index].id}
                style={style}
              />
            )}
          </List>
        </Box>
      </Box>

      {/* Footer / Stats */}
      <Box sx={{
        px: 2,
        py: 1,
        borderTop: '1px solid',
        borderColor: 'divider',
        bgcolor: 'grey.50',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="body2" color="text.secondary">
          Всего документов: <strong>{documents.length}</strong>
        </Typography>
      </Box>
    </Paper>
  );
};

// --- Subcomponents ---

const ListHeader: React.FC = () => (
  <Box sx={{
    display: 'grid',
    gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
    gap: 1,
    px: 2,
    py: 1.5,
    bgcolor: 'transparent',
    borderBottom: '1px solid',
    borderColor: 'divider',
    alignItems: 'center'
  }}>
    <HeaderCell>Тип</HeaderCell>
    <HeaderCell>Номер</HeaderCell>
    <HeaderCell>Статус</HeaderCell>
    <HeaderCell>Отправитель</HeaderCell>
    <HeaderCell>Получатель</HeaderCell>
    <HeaderCell>Дата</HeaderCell>
    <HeaderCell align="right">Позиций</HeaderCell>
    <Box />
  </Box>
);

const HeaderCell: React.FC<{ children: React.ReactNode; align?: 'left' | 'right' }> = ({ children, align = 'left' }) => (
  <Typography
    variant="subtitle2"
    color="text.secondary"
    fontWeight={600}
    align={align}
    sx={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}
  >
    {children}
  </Typography>
);

// Виртуализированная строка документа с оптимизацией
interface VirtualizedDocumentRowProps {
  document: Document;
  currentUsername: string;
  isLoadingDocument: boolean;
  onDocumentClick: (document: Document) => void;
  onMenuClick: (event: React.MouseEvent<HTMLElement>, document: Document) => void;
  style: React.CSSProperties;
  isHighlighted?: boolean;
}

const VirtualizedDocumentRow: React.FC<VirtualizedDocumentRowProps> = React.memo(({
  document,
  currentUsername,
  isLoadingDocument,
  onDocumentClick,
  onMenuClick,
  style,
  isHighlighted
}) => {
  const theme = useTheme();

  // Memoized values
  const formattedDate = useMemo(() =>
    formatDate(document.createdAtISO || document.createdAt),
    [document.createdAtISO, document.createdAt]
  );

  const isNew = useMemo(() =>
    isNewDocument(document, currentUsername),
    [document, currentUsername]
  );

  const handleClick = useCallback(() => {
    if (!isLoadingDocument) {
      onDocumentClick(document);
    }
  }, [isLoadingDocument, onDocumentClick, document]);

  const handleMenuClick = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    onMenuClick(e, document);
  }, [onMenuClick, document]);

  // Styles calculation
  const isScanningMode = document.status === 12;
  const statusColor = getStatusColor(document.status || 0);

  // Background logic
  const getBgColor = () => {
    if (isScanningMode) return alpha(theme.palette.warning.main, 0.08);
    if (isHighlighted) return alpha(theme.palette.success.main, 0.08);
    return 'transparent'; // Hover handled by CSS
  };

  return (
    <Box
      style={style}
      onClick={handleClick}
      sx={{
        display: 'grid',
        gridTemplateColumns: GRID_TEMPLATE_COLUMNS,
        gap: 1,
        px: 2,
        alignItems: 'center',
        borderBottom: '1px solid',
        borderColor: 'divider',
        cursor: isLoadingDocument ? 'wait' : 'pointer',
        bgcolor: getBgColor(),
        transition: 'all 0.2s ease',
        '&:hover': {
          bgcolor: isHighlighted
            ? alpha(theme.palette.success.main, 0.15)
            : alpha(theme.palette.primary.main, 0.04),
          '& .MuiTypography-root': { color: 'text.primary' } // Darken text on hover
        },
        ...(isScanningMode && {
          borderLeft: `3px solid ${theme.palette.warning.main}`,
          pl: '13px' // 16px - 3px border
        })
      }}
    >
      {/* Тип */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Tooltip title={
          document.type === 4
            ? 'Возврат'
            : document.isIncoming
              ? 'Входящее перемещение'
              : 'Исходящее перемещение'
        }>
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 32,
            height: 32,
            borderRadius: '8px',
            bgcolor: document.type === 4
              ? alpha(theme.palette.info.main, 0.1)
              : document.isIncoming
                ? alpha(theme.palette.success.main, 0.1)
                : alpha(theme.palette.secondary.main, 0.1),
            color: document.type === 4
              ? theme.palette.info.main
              : document.isIncoming
                ? theme.palette.success.main
                : theme.palette.secondary.main
          }}>
            {document.type === 4 ? <AssignmentIcon fontSize="small" /> : <SwapHorizIcon fontSize="small" />}
          </Box>
        </Tooltip>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" fontWeight={500}>
              {document.type === 4 ? 'Возврат' : 'Перемещение'}
            </Typography>
            {document.type === 4 && (
              <Chip
                label={document.requestMarks === 1 || document.isMarkedProduct ? 'КМ' : 'Без КМ'}
                size="small"
                color={document.requestMarks === 1 || document.isMarkedProduct ? 'primary' : 'default'}
                variant={document.requestMarks === 1 || document.isMarkedProduct ? 'filled' : 'outlined'}
                sx={{
                  height: 16,
                  fontSize: '0.62rem',
                  fontWeight: 600,
                  '& .MuiChip-label': { px: 0.5 }
                }}
              />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
            {document.isIncoming ? 'Входящее' : 'Исходящее'}
          </Typography>
        </Box>
      </Box>

      {/* Номер */}
      <Box>
        <Typography variant="body2" fontWeight={600} fontFamily="monospace">
          #{getDocumentNumber(document)}
        </Typography>
        {isNew && (
          <Chip
            label="NEW"
            size="small"
            color="success"
            sx={{
              height: 16,
              fontSize: '0.65rem',
              fontWeight: 'bold',
              '& .MuiChip-label': { px: 0.5 }
            }}
          />
        )}
      </Box>

      {/* Статус */}
      <Box>
        <Tooltip title={getStatusText(document.status || 0, document)} arrow>
          <Chip
            label={getStatusText(document.status || 0, document)}
            size="small"
            sx={{
              height: 24,
              fontSize: '0.75rem',
              fontWeight: 500,
              maxWidth: 140,
              '& .MuiChip-label': {
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              },
              bgcolor: alpha((statusColor !== 'default' && theme.palette[statusColor]?.main) || theme.palette.grey[500], 0.1),
              color: (statusColor !== 'default' && theme.palette[statusColor]?.main) || theme.palette.grey[700],
              border: '1px solid',
              borderColor: alpha((statusColor !== 'default' && theme.palette[statusColor]?.main) || theme.palette.grey[500], 0.2)
            }}
          />
        </Tooltip>
      </Box>

      {/* Отправитель */}
      <Typography variant="body2" color="text.secondary" noWrap title={document.fromPharmacy || ''}>
        {document.fromPharmacy || '-'}
      </Typography>

      {/* Получатель */}
      <Typography variant="body2" color="text.secondary" noWrap title={document.toPharmacy || ''}>
        {document.toPharmacy || '-'}
      </Typography>

      {/* Дата */}
      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem' }}>
        {formattedDate}
      </Typography>

      {/* Позиции */}
      <Typography variant="body2" align="right" fontWeight={500}>
        {document.itemsCount || 0}
      </Typography>

      {/* Действия */}
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        {((document.status === 9 || isErrorStatus(document.status || 0)) && !document.isIncoming) && (
          <IconButton
            size="small"
            onClick={handleMenuClick}
            sx={{
              color: 'text.secondary',
              '&:hover': { color: 'primary.main', bgcolor: alpha(theme.palette.primary.main, 0.1) }
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        )}
        {isLoadingDocument && <CircularProgress size={16} />}
      </Box>
    </Box>
  );
}, (prev, next) => {
  return (
    prev.document.id === next.document.id &&
    prev.document.status === next.document.status &&
    prev.document.updateDate === next.document.updateDate &&
    prev.isLoadingDocument === next.isLoadingDocument &&
    prev.currentUsername === next.currentUsername &&
    prev.isHighlighted === next.isHighlighted
  );
});

VirtualizedDocumentRow.displayName = 'VirtualizedDocumentRow';

export default DocumentList;
